import { createContext, useCallback, useContext, useEffect, useReducer } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WorkspaceContext = createContext(null);
const STORAGE_KEY = "probe_workspace_id";

const initial = {
  workspaces: null,
  currentId: typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null,
  loading: true,
};

function reducer(state, action) {
  switch (action.type) {
    case "set_workspaces":
      return { ...state, workspaces: action.workspaces, loading: false };
    case "set_current":
      return { ...state, currentId: action.id };
    case "reset":
      return { workspaces: null, currentId: null, loading: false };
    case "start_loading":
      return { ...state, loading: true };
    default:
      return state;
  }
}

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [state, dispatch] = useReducer(reducer, initial);
  const { workspaces, currentId, loading } = state;

  const refresh = useCallback(async () => {
    dispatch({ type: "start_loading" });
    const { data } = await api.get("/workspaces");
    dispatch({ type: "set_workspaces", workspaces: data });
    const storedId = localStorage.getItem(STORAGE_KEY);
    const matched = data.find((w) => w.id === storedId);
    if (matched) {
      dispatch({ type: "set_current", id: matched.id });
    } else if (data.length > 0) {
      localStorage.setItem(STORAGE_KEY, data[0].id);
      dispatch({ type: "set_current", id: data[0].id });
    } else {
      localStorage.removeItem(STORAGE_KEY);
      dispatch({ type: "set_current", id: null });
    }
  }, []);

  useEffect(() => {
    if (user && typeof user === "object") {
      refresh().catch(() => dispatch({ type: "set_workspaces", workspaces: [] }));
    } else if (user === false) {
      localStorage.removeItem(STORAGE_KEY);
      dispatch({ type: "reset" });
    }
  }, [user, refresh]);

  const switchTo = (id) => {
    localStorage.setItem(STORAGE_KEY, id);
    dispatch({ type: "set_current", id });
  };

  const current = workspaces?.find((w) => w.id === currentId) || null;

  return (
    <WorkspaceContext.Provider value={{ workspaces, current, currentId, loading, switchTo, refresh }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export const useWorkspace = () => useContext(WorkspaceContext);
